import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WebSocketBridge } from 'django-channels';
import { Subject } from 'rxjs';
import { delay, retryWhen } from 'rxjs/operators';
import { EVENTS, SERVER_DISCONNECTION, Utils } from 'src/app/_helpers';
import {
    SocketAction,
    SocketAuthResponse, SocketError,
    SocketGenericMessage, SocketMessageQueue,
} from '../../../../../sharedweb/app.interface';
import {
    AIScreenerStreams,
    MarketStreams,
    SockRoutes,
    SocketCommands,
    SocketConnections,
    SocketErrorCodes,
    StreamTypes
} from '../../../../../sharedweb/app.structs';
import { applicationUrls } from '../../../../../sharedweb/helpers/urls';
import { EventsService } from '../events/events.service';

@Injectable({
    providedIn: 'root'
})
export class SocketService {

    private _webSocketBridges: Map<SocketConnections, WebSocketBridge> = new Map();
    private _streams: Map<SocketConnections, Map<StreamTypes, Subject<any>>> = new Map();
    private _channelObservers: Map<SocketConnections, Subject<any>> = new Map();

    private _connectionQueueMap: Map<SocketConnections, SocketMessageQueue> = new Map();
    private _routes: { [key: string]: Subject<any> } = {};
    private _lastSubscriptionMap: Map<SocketConnections, any[]> = new Map();
    private lastPongTime: number;
    private lastPingTime: number;
    private websocketTimeout = 5000;
    private observerInterval;
    private statusInterval;

    private firstConnection = true;

    constructor(
        private _httpClient: HttpClient,
        private _eventService: EventsService

    ) {
        Object.values(SocketConnections).forEach(connection => {
            this._lastSubscriptionMap.set(connection, []);
        });
    }

    private _routeSubject(route: SockRoutes): Subject<any> {
        // let sockRoute = this._routes[route];
        if (!this._routes[route]) {
            this._routes[route] = new Subject<any>();
        }
        return this._routes[route];
    }

    async loggedInCallback() {
        if (!this.checkSocketConnected(SocketConnections.order)) {
            this.connect(SocketConnections.order);
        } else {
            this.clearStatusInterval();
    
    
            let authResponse = <SocketAuthResponse>await this.authenticateSocket();
            let token = authResponse.token;
            const authRequest: SocketGenericMessage = {
                command: SocketCommands.authenticate,
                token: token
            };
            const a = this._webSocketBridges[SocketConnections.order].send(authRequest);
            this._eventService.publish(EVENTS.SOCKET_AUTHENTICATED, this.firstConnection);
        }
    }

    checkSocketConnected(connection: SocketConnections) {
        return this._webSocketBridges[connection] ? true : false;
    }

    authenticateSocket() {
        const url = applicationUrls.socketAuthUrl;
        return this._httpClient.get(url)
            .pipe(
                // Will continue to retry fetching websocket token in case of some error.
                // This will most likly occur in case of network disconnect or webserver
                // restart.
                retryWhen(e => {
                    return e.pipe(delay(this.websocketTimeout / 2));
                })
            ).toPromise();
    }

    observeWebsocketDisconnect(webSocketBridge) {

        // If there was already a interval running, stop that before starting a new one.
        // This will happen in case of reconnection.
        if (this.observerInterval) {
            clearInterval(this.observerInterval);
        }

        // Will be checking connection status every half time-out - 1sec (1sec to compensate for network transfer time).
        this.observerInterval = setInterval(() => {
            // If the last ping time was more than the expected timeout, disconnect and reconnect
            // socket
            if ((this.lastPingTime - this.lastPongTime) > this.websocketTimeout) {
                console.error(`STALE WEBSOCKET CONNECTION... Reconnecting  LastPingTime ${this.lastPingTime} LastPongTime ${this.lastPongTime}`);
                this.disconnectConnection(SocketConnections.order);
                this.connect(SocketConnections.order);
                this._eventService.publish(SERVER_DISCONNECTION.WEBSERVER, true);
                clearInterval(this.observerInterval);
                this.observerInterval = null;
                return;
            }
            const pingRequest: SocketGenericMessage = {
                command: SocketCommands.ping,
            };
            this.lastPingTime = Date.now();
            webSocketBridge.send(pingRequest);
        }, (this.websocketTimeout / 2) - 1e3);
    }

    async connect(connection: SocketConnections) {
        if (this._webSocketBridges[connection]) {
            console.log('Socket Already connected not sending request', connection);
            return;
        }
        console.log('Connecting to socket', connection);

        const channelObserver: Subject<any> = new Subject();

        const webSocketBridge = new WebSocketBridge();
        this._webSocketBridges[connection] = webSocketBridge;

        this._connectionQueueMap[connection] = <SocketMessageQueue>{
            isConnectionOpened: false,
            messages: []
        };
        await this._connectAndAuthorizeSocket(webSocketBridge, connection, channelObserver);

        webSocketBridge.listen((message, _) => {
            // Authentication response has the server configued time-out in it.
            // Will start observing timeout according to that time.
            if (message && message.message === 'Authentication Successful') {
                this._eventService.publish(SERVER_DISCONNECTION.WEBSERVER, false);
                this.websocketTimeout = message.timeout * 1000;
                this.lastPongTime = Date.now();
                this.observeWebsocketDisconnect(webSocketBridge);
            }
        })

        const streamMap = new Map<StreamTypes, Subject<any>>();
        for (const StreamType in StreamTypes) {
            if (StreamType) {
                streamMap[StreamTypes[StreamType]] = new Subject();
                this._webSocketBridges[connection].demultiplex(StreamTypes[StreamType], (action: SocketAction, stream) => {
                    streamMap[stream].next(action);
                    const orderBookData = action.order_book_data;
                    const tradeBookData = action.trade_book_data;
                    const positionsData = action.positions_data;
                    const marginsData = action.margins_data;
                    const bocoData = action.boco_data;
                    const rejectionData = action.rejections_data;
                    const holdingsData = action.holdings_data;
                    const mtfData = action.mtf_holdings_data;
                    const mtfMarginData = action.mtf_holdings_margin_data;
                    const broadCastData = action.broadcast_messages;
                    const alertsData = action.alerts_data;
                    const command = action.command;
                    const productConversionSummary = action.product_conversions_data;
                    const mapping = action.mapping;
                    const kyc_status = action.kyc_status;
                    const dms_connection_status = action.dms_connection_status;
                    const hft_connection_status = action.hft_connection_status;
                    const go_server_status = action.go_server_status;
                    const module_connection_status = action.module_conection_status;
                    const logout_reason = action.logout_reason;
                    const mutualFundData = action.mutual_fund_holdings_data;
                    const pledge_update = action.pledge_update;
                    const gttBookData = action.gtt_order_notification;
                    const amoTimingsUpdate = action.amo_timings;
                    const smartOrdersUpdate = action.smart_order_notification;
                    const payoutParamsUpdate = action.payout_params;

                    if (module_connection_status) { 
                        this._eventService.publish(SERVER_DISCONNECTION.SNAPSHOTS, {
                            goServerDisConnected: !module_connection_status.go_server_status,
                            hftDMSDisConnected: !module_connection_status.hft_connected || !module_connection_status.dms_connected
                        });
                    } 

                    if (hft_connection_status) { 
                        this._eventService.publish(SERVER_DISCONNECTION.HFTDMS, !hft_connection_status.connected);
                    } 
                    
                    if(dms_connection_status) {
                        this._eventService.publish(SERVER_DISCONNECTION.HFTDMS, !dms_connection_status.connected);
                    } 
                    
                    if(go_server_status) {   
                        this._eventService.publish(SERVER_DISCONNECTION.GOSERVER, !go_server_status.connected);
                    }

                    const funds_status = action.funds_status;
                    const foBannedSymbols = action.fo_ban;
                    const preIPORes = action.preIpo;
                    const ipoRes = action.ipo;
                    const edis = action.edis;
                    // Any Message received from socket will be treated as heartbeat.
                    this.lastPongTime = Date.now();
                    this._routeSubject(SockRoutes.rejection).next(rejectionData);
                    this._routeSubject(SockRoutes.orderbook).next(orderBookData);
                    this._routeSubject(SockRoutes.tradebook).next(tradeBookData);
                    this._routeSubject(SockRoutes.postions).next(positionsData);
                    this._routeSubject(SockRoutes.bocoData).next(bocoData);
                    this._routeSubject(SockRoutes.holding).next(holdingsData);
                    this._routeSubject(SockRoutes.mtf).next(mtfData);
                    this._routeSubject(SockRoutes.mtfMargin).next(mtfMarginData);
                    this._routeSubject(SockRoutes.broadcastMessages).next(broadCastData);
                    this._routeSubject(SockRoutes.alertsData).next(alertsData);
                    this._routeSubject(SockRoutes.commands).next({command, logout_reason});
                    this._routeSubject(SockRoutes.productConversionSummary).next(productConversionSummary);
                    this._routeSubject(SockRoutes.mapping).next(mapping);
                    this._routeSubject(SockRoutes.kyc_status).next(kyc_status);
                    this._routeSubject(SockRoutes.dms_connection_status).next(dms_connection_status);
                    this._routeSubject(SockRoutes.funds_status).next(funds_status);
                    this._routeSubject(SockRoutes.fo_ban).next(foBannedSymbols);
                    this._routeSubject(SockRoutes.ipo).next({ipoRes: preIPORes, isPreIPO: true});
                    this._routeSubject(SockRoutes.ipo).next({ipoRes, isPreIPO: false});
                    this._routeSubject(SockRoutes.edis).next(edis);
                    this._routeSubject(SockRoutes.mutualFund).next(mutualFundData);
                    this._routeSubject(SockRoutes.pledge).next(pledge_update);
                    this._routeSubject(SockRoutes.gttbook).next(gttBookData);
                    this._routeSubject(SockRoutes.liveMessages).next(amoTimingsUpdate);
                    this._routeSubject(SockRoutes.smart_orders_update).next(smartOrdersUpdate);
                    this._routeSubject(SockRoutes.payout_params).next(payoutParamsUpdate);
                });
                this._streams[connection] = streamMap;
            }
        }

        this._channelObservers[connection] = channelObserver;
        return channelObserver;
    }

    connectToMarketFeed(connection: SocketConnections) {
        if (this._webSocketBridges[connection]) {
            console.log('Socket Already connected not sending request', connection);
            return;
        }
        console.log('Connecting to socket', connection);

        const channelObserver: Subject<any> = new Subject();

        const webSocketBridge = new WebSocketBridge();
        this._webSocketBridges[connection] = webSocketBridge;

        this._connectionQueueMap[connection] = <SocketMessageQueue>{
            isConnectionOpened: false,
            messages: []
        };

        this._connectAndAuthorizeSocket(webSocketBridge, connection, channelObserver);

        const streamMap = new Map<MarketStreams, Subject<any>>();
        for (const streamType in MarketStreams) {
            if (streamType) {
                streamMap[MarketStreams[streamType]] = new Subject();
                this._webSocketBridges[connection].demultiplex(MarketStreams[streamType], (action: SocketAction, stream) => {
                    // console.log('listen', action, stream);
                    streamMap[stream].next(action);
                    const orderDepth = action.depth;
                    const quotesData = action.quote;
                    const openInterest = action.oi;
                    const dpr = action.dpr;
                    const indices = action.indices;
                    const notices = action.notice;
                    const marketSession = action.session;

                    this._routeSubject(SockRoutes.orderdepth).next(orderDepth);
                    this._routeSubject(SockRoutes.marketwatch).next(quotesData);
                    this._routeSubject(SockRoutes.openInterest).next(openInterest);
                    this._routeSubject(SockRoutes.dpr).next(dpr);
                    this._routeSubject(SockRoutes.indices).next(indices);
                    this._routeSubject(SockRoutes.notices).next(notices);
                    this._routeSubject(SockRoutes.marketSession).next(marketSession);
                });
                this._streams[connection] = streamMap;
            }
        }

        this._channelObservers[connection] = channelObserver;
        return channelObserver;
    }

    subscribeToMtf(subscribe: boolean) {
        this.send(SocketConnections.order, {
            command: SocketCommands.subscribeMtf,
            subscribe: subscribe
        });
    }

    connectToScreenerSocket(connection: SocketConnections) {
        if (this._webSocketBridges[connection]) {
            console.log('Socket Already connected not sending request', connection);
            return;
        }
        console.log('Connecting to socket', connection);

        const channelObserver: Subject<any> = new Subject();

        const webSocketBridge = new WebSocketBridge();
        this._webSocketBridges[connection] = webSocketBridge;

        this._connectionQueueMap[connection] = <SocketMessageQueue>{
            isConnectionOpened: false,
            messages: []
        };

        this._connectAndAuthorizeSocket(webSocketBridge, connection, channelObserver);

        const streamMap = new Map<AIScreenerStreams, Subject<any>>();
        for (const streamType in AIScreenerStreams) {
            if (streamType) {
                streamMap[AIScreenerStreams[streamType]] = new Subject();
                this._webSocketBridges[connection].demultiplex(AIScreenerStreams[streamType], (action: SocketAction, stream) => {
                    // console.log('listen', action, stream);
                    streamMap[stream].next(action);
                    this._routeSubject(SockRoutes.screener).next(action);
                });
                this._streams[connection] = streamMap;
            }
        }

        this._channelObservers[connection] = channelObserver;
        return channelObserver;
    }

    retryStatusInterval(connection: any) {
        this.clearStatusInterval();

        this.statusInterval = setInterval(() => {
            const statusRequest: SocketGenericMessage = {
                command: SocketCommands.connection_status,
            };
            this._webSocketBridges[connection].send(statusRequest);
        }, 1000);
    }

    private async _connectAndAuthorizeSocket(webSocketBridge, connection, channelObserver) {
        let authResponse;
        let token;

        switch (connection) {
            case SocketConnections.order:
                webSocketBridge.connect(applicationUrls.SOCKET_URL + connection);
                break;
            case SocketConnections.marketData:
                webSocketBridge.connect(applicationUrls.MARKET_DATA_URL, 'http-only');
                break;
        }

        webSocketBridge.socket.addEventListener('open', async () => {
            if (Utils.isAuthenticated()) {
                authResponse = <SocketAuthResponse>await this.authenticateSocket();
                token = authResponse.token;
                const authRequest: SocketGenericMessage = {
                    command: SocketCommands.authenticate,
                    token: token
                };
                const a = this._webSocketBridges[connection].send(authRequest);
                this._eventService.publish(EVENTS.SOCKET_AUTHENTICATED, this.firstConnection);
            } else {
                this._eventService.publish(SERVER_DISCONNECTION.WEBSERVER, false);
                this.observeWebsocketDisconnect(webSocketBridge);
                this.retryStatusInterval(connection);
            }
            this._eventService.publish(EVENTS.CHECK_INTERNET);

            this._connectionQueueMap[connection].isConnectionOpened = true;
            this.firstConnection = !this.firstConnection;

            // Sending event whenever market data socket is connected.
            if (connection === SocketConnections.marketData) {
                this._eventService.publish(EVENTS.SOCKET_CONNECTED, true);
            }

            const messages = this._connectionQueueMap[connection].messages;

            while (messages.length) {
                this.send(connection, messages.shift());
            }
            this._resubscribe(connection);
        });

        webSocketBridge.socket.addEventListener('close', () => {
            this._eventService.publish(SERVER_DISCONNECTION.WEBSERVER, true);
            this._eventService.publish(EVENTS.CHECK_INTERNET);
            this.clearStatusInterval();
            this._connectionQueueMap[connection].isConnectionOpened = false;
        });

        webSocketBridge.listen(async (action, stream) => {
            // If Stream = null then action is Socket Message
            // console.log('Action, Stream', action, stream);
            if (stream === null) {
                /**
                 * Publish to Channel Subscribers
                 * only if Stream is null
                 */
                channelObserver.next(action);
            }

            // Listen for Authentication Errors
            const sockError = <SocketError>action;
            if (sockError.error &&
                (sockError.code === SocketErrorCodes.InvalidSocket || sockError.code === SocketErrorCodes.SocketAuthentication)) {
                if (Utils.isAuthenticated()) {
                    const authResponseReconnect = <SocketAuthResponse>await this.authenticateSocket();
                    console.log('AUTH RESPONSE', authResponseReconnect.token);
                    const refreshToken = authResponseReconnect.token;
                    /**
                     * If Socket Connection is Interrupted,
                     * we need to reAuthenticate and reConnect
                     */
                    console.log('Reconnecting');
                    // _webSocketBridge.socket.close();
                    const message: SocketGenericMessage = {
                        command: SocketCommands.authenticate,
                        token: refreshToken
                    };
                    this._webSocketBridges[connection].send(message);
                    this._resubscribe(connection);
                } else {
                    this.retryStatusInterval(connection);
                }
            }
        });
    }


    private _resubscribe(connection: SocketConnections) {
        const previousSubscription = this._lastSubscriptionMap.get(connection);
        this._lastSubscriptionMap.set(connection, []);
        console.log(`Resubscribing ${previousSubscription.length} symbols`);
        if (previousSubscription.length) {
            previousSubscription.forEach(subscription => {
                this.send(connection, subscription);
            });
        }
    }

    /**
     * Listen to Socket Connection's Direct Feed
     * @param connection Socket Connection Path
     */
    getConnectionFeed(connection: SocketConnections): Subject<any> {
        return this._channelObservers[connection];
    }

    /**
     * Listen to Stream Feed
     * @param connection Socket Connection Path
     * @param streamType Stream to Listen
     */
    getStreamFeed(connection: SocketConnections, streamType: StreamTypes): Subject<any> {
        return this._streams[connection][streamType];
    }

    getFeed(connection: SocketConnections, route: SockRoutes) {
        return this._routeSubject(route);
    }

    /**
     * Send Message to Socket Connection
     * @param connection Socket Connection Path
     * @param message Message to Send
     */
    send(connection: SocketConnections, message: SocketGenericMessage) {
        /** Commenting logs as they have substantial overhead on performance, anyone can uncomment for logging.
         *  TODO: Make logger for frontend.
         *
         * console.log('sending to socket', connection);
         */
        const connectionQueue = this._connectionQueueMap[connection];
        if (!connectionQueue.isConnectionOpened) {
            console.log('Connection Not Opened, Adding to Queue', message);
            connectionQueue.messages.push(message);
        } else {
            if (message.command.split('-')[0] === SocketCommands.subscribe) {
                // console.log('Setting Subscribe for', connection, message);
                this._lastSubscriptionMap.get(connection).push(message);
            }
            this._webSocketBridges[connection].send(message);
        }
    }

    /**
     * Send Message to Stream
     * @param connection Socket Connection Path
     * @param to Stream to Engage
     * @param message Message to Send
     */
    sendToStream(connection: SocketConnections, to: StreamTypes, message: Object) {
        this._webSocketBridges[connection].stream(to).send(message);
    }


    disconnectAllSockets() {
        for (const connection in this._webSocketBridges) {
            this.disconnectConnection(connection as SocketConnections);
        }

        this.clearStatusInterval();
    }

    clearStatusInterval() {
        if (this.statusInterval) {
            clearInterval(this.statusInterval);
            this.statusInterval = null;
        }
    }

    disconnectConnection(connection: SocketConnections) {
        if (this._webSocketBridges[connection] && this._webSocketBridges[connection].socket) {
            this._webSocketBridges[connection].socket.close(1000, '', { keepClosed: true });
            // Remove Connection Reference
            delete this._webSocketBridges[connection];
        }
    }

    isConnected(connection: SocketConnections) {
        try {
            return this._connectionQueueMap[connection].isConnectionOpened;
        } catch {
            return false;
        }
    }

    setEventService(eventService: EventsService) {
        this._eventService = eventService;
    }
}
